import { Tag } from 'antd'
import { ZoneEditPanel } from 'features/libra/stepComponents/niiReport/cards/ZoneEditPanel'
import { NiiSourcesZoneParams } from 'features/libra/stepComponents/niiReport/niiReportApi'
import { useGeckoLocale } from 'features/locale'
import { Edit3, Trash2 } from 'lucide-react'
import { FC, useMemo } from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

const ZoneRowContainer = styled.div`
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  overflow: hidden;
`

const ZoneMainRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
`

const ZoneInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`

const ZoneColorIndicator = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background-color: ${({ color }) => color};
  border: 1px solid rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
`

const ZoneName = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.defaultText};
  flex: 1;
`

const ZoneActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ActionButton = styled.button<{ $isDelete?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${({ theme, $isDelete }) =>
      $isDelete ? '#fee' : theme.forecast.background3};
    color: ${({ $isDelete }) => ($isDelete ? '#dc2626' : 'inherit')};
  }
`

export const ZoneRow: FC<{
  zone: NiiSourcesZoneParams
  isEditing: boolean
  totalAssetArea?: import('types').GeckoMeasurement
  onEdit: () => void
  onDelete: () => void
  onUpdate: (updates: Partial<NiiSourcesZoneParams>) => void
  onCancelEdit: () => void
}> = ({
  zone,
  isEditing,
  totalAssetArea,
  onEdit,
  onDelete,
  onUpdate,
  onCancelEdit,
}) => {
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const areaUnit = getPreferredMeasurementUnit(
    GeckoMeasurementUnit.SquareMeters
  )

  const formattedArea = useMemo(() => {
    return zone.calculated_area_sq_m && zone.calculated_area_sq_m > 0
      ? GeckoMeasurement.CreateFromValue(
          zone.calculated_area_sq_m,
          GeckoMeasurementUnit.SquareMeters
        ).convertTo(areaUnit)
      : undefined
  }, [zone.calculated_area_sq_m, areaUnit])

  const zoneCoverage = useMemo(() => {
    if (!totalAssetArea || !zone.calculated_area_sq_m) return undefined
    const totalAssetAreaSqM = totalAssetArea.convertTo(
      GeckoMeasurementUnit.SquareMeters
    ).num
    return totalAssetAreaSqM > 0
      ? (zone.calculated_area_sq_m / totalAssetAreaSqM) * 100
      : 0
  }, [zone.calculated_area_sq_m, totalAssetArea])

  const zoneColor = zone.color || '#3B82F6'

  return (
    <ZoneRowContainer>
      <ZoneMainRow>
        <ZoneInfo>
          <ZoneColorIndicator color={zoneColor} />
          <ZoneName>{zone.title}</ZoneName>
          {formattedArea ? (
            <Tag color='green'>
              {formattedArea.toNumber(areaUnit).toFixed(2)} {areaUnit.name}
            </Tag>
          ) : (
            <Tag color='orange'>Area not calculated</Tag>
          )}
          {zoneCoverage !== undefined && (
            <Tag color='blue'>{zoneCoverage.toFixed(1)}% coverage</Tag>
          )}
        </ZoneInfo>
        <ZoneActions>
          <ActionButton onClick={onEdit} title='Edit zone'>
            <Edit3 size={14} />
          </ActionButton>
          <ActionButton $isDelete onClick={onDelete} title='Delete zone'>
            <Trash2 size={14} />
          </ActionButton>
        </ZoneActions>
      </ZoneMainRow>

      {/* Edit panel appears directly under this zone row */}
      {isEditing && (
        <ZoneEditPanel zone={zone} onSave={onUpdate} onCancel={onCancelEdit} />
      )}
    </ZoneRowContainer>
  )
}
